import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import DohopHeader from "../../components/dohop/DohopHeader";
import DohopFlightCard from "../../components/dohop/DohopFlightCard";
import DohopLoadingSpinner from "../../components/dohop/DohopLoadingSpinner";
import {
    DohopOffer,
    DohopAirShoppingResponse,
    DohopDataLists,
} from "../../types/dohop";
import {
    searchDohopFlights,
    buildSearchRequest,
    decodeSearchParams,
} from "../../services/dohopApi";

const DohopSearchResults: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [offers, setOffers] = useState<DohopOffer[]>([]);
    const [dataLists, setDataLists] = useState<DohopDataLists | null>(null);
    const [searchDetails, setSearchDetails] = useState<{
        origin: string;
        destination: string;
        date: string;
        passengers: number;
    } | null>(null);

    useEffect(() => {
        const fetchFlights = async () => {
            const encodedParams = searchParams.get("search");

            if (!encodedParams) {
                setError("No search parameters provided");
                setLoading(false);
                return;
            }

            try {
                const decoded = decodeSearchParams(encodedParams);
                if (!decoded) {
                    setError("Invalid search parameters");
                    setLoading(false);
                    return;
                }

                setSearchDetails({
                    origin: decoded.origin,
                    destination: decoded.destination,
                    date: decoded.departureDate,
                    passengers:
                        decoded.adults + decoded.children + decoded.infants,
                });

                const request = buildSearchRequest(
                    decoded.origin,
                    decoded.destination,
                    decoded.departureDate,
                    decoded.adults,
                    decoded.children,
                    decoded.infants
                );

                const response: DohopAirShoppingResponse =
                    await searchDohopFlights(request);

                if (response.offers && response.offers.length > 0) {
                    setOffers(response.offers);
                    setDataLists(response.data_lists);
                } else {
                    setError(
                        "No flights found for your search. Try different dates or destinations."
                    );
                }
            } catch (err) {
                console.error("Error fetching flights:", err);
                setError("Failed to search flights. Please try again later.");
            } finally {
                setLoading(false);
            }
        };

        fetchFlights();
    }, [searchParams]);

    const handleSelectOffer = (offer: DohopOffer) => {
        // Store the selected offer in sessionStorage
        sessionStorage.setItem("selectedDohopOffer", JSON.stringify(offer));
        sessionStorage.setItem("dohopDataLists", JSON.stringify(dataLists));

        // Navigate to checkout
        navigate("/dohop/checkout");
    };

    const handleNewSearch = () => {
        navigate("/dohop");
    };

    return (
        <div className="min-h-screen bg-dohop-gray">
            <DohopHeader />

            {/* Search summary bar */}
            {searchDetails && (
                <div className="bg-white border-b border-gray-200 py-4">
                    <div className="max-w-7xl mx-auto px-4">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <span className="font-bold text-dohop-navy">
                                        {searchDetails.origin}
                                    </span>
                                    <svg
                                        className="w-4 h-4 text-gray-400"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M14 5l7 7m0 0l-7 7m7-7H3"
                                        />
                                    </svg>
                                    <span className="font-bold text-dohop-navy">
                                        {searchDetails.destination}
                                    </span>
                                </div>
                                <span className="text-gray-400">|</span>
                                <span className="text-gray-600">
                                    {new Date(
                                        searchDetails.date
                                    ).toLocaleDateString("en-US", {
                                        weekday: "short",
                                        month: "short",
                                        day: "numeric",
                                    })}
                                </span>
                                <span className="text-gray-400">|</span>
                                <span className="text-gray-600">
                                    {searchDetails.passengers}{" "}
                                    {searchDetails.passengers === 1
                                        ? "passenger"
                                        : "passengers"}
                                </span>
                            </div>
                            <button
                                onClick={handleNewSearch}
                                className="text-dohop-blue font-medium hover:underline"
                            >
                                Modify Search
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Main content */}
            <div className="max-w-7xl mx-auto px-4 py-8">
                {loading && <DohopLoadingSpinner />}

                {error && (
                    <div className="bg-white rounded-lg shadow-md p-8 text-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg
                                className="w-8 h-8 text-red-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                />
                            </svg>
                        </div>
                        <h2 className="text-xl font-bold text-dohop-navy mb-2">
                            No Flights Found
                        </h2>
                        <p className="text-gray-600 mb-6">{error}</p>
                        <button
                            onClick={handleNewSearch}
                            className="px-6 py-3 bg-dohop-blue text-white rounded-lg font-semibold hover:bg-dohop-blue-dark transition-colors"
                        >
                            Try Another Search
                        </button>
                    </div>
                )}

                {!loading && !error && offers.length > 0 && dataLists && (
                    <>
                        <div className="mb-6">
                            <h1 className="text-2xl font-bold text-dohop-navy">
                                {offers.length} flight
                                {offers.length !== 1 ? "s" : ""} found
                            </h1>
                            <p className="text-gray-600">
                                Select a flight to continue to checkout
                            </p>
                        </div>

                        <div className="space-y-4">
                            {offers.map((offer) => (
                                <DohopFlightCard
                                    key={offer.offer_id}
                                    offer={offer}
                                    dataLists={dataLists}
                                    onSelect={handleSelectOffer}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default DohopSearchResults;
